import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { CONTACT, IMAGES } from "@/data/classes";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/classes", label: "Classes" },
  { to: "/schedule", label: "Schedule" },
  { to: "/group-training", label: "Group Training" },
  { to: "/about", label: "About" },
  { to: "/faqs", label: "FAQs" },
  { to: "/contact", label: "Contact" },
];

export function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  React.useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-white border-b border-gray-200"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3">
          <img
            src={IMAGES.logo}
            alt="Rhythm Check Training"
            className="h-10 md:h-12 w-auto"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={`nav-link-${link.to.replace("/", "") || "home"}`}
              end={link.to === "/"}
              className={({ isActive }) =>
                `text-sm font-semibold tracking-wide transition-colors ${
                  isActive ? "text-[#d90429]" : "text-gray-900 hover:text-[#d90429]"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <a
            href={CONTACT.phoneHref}
            data-testid="header-phone-link"
            className="flex items-center gap-2 text-sm font-semibold text-gray-900 hover:text-[#d90429] transition-colors"
          >
            <Phone className="w-4 h-4" />
            {CONTACT.phone}
          </a>
          <Link
            to="/schedule"
            data-testid="header-signup-cta"
            className="inline-flex items-center rounded-md bg-[#d90429] px-5 py-3 text-sm font-bold text-white hover:bg-black transition-colors focus-ring"
          >
            Sign Up for a Class
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden p-2 text-gray-900"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-gray-200 bg-white" data-testid="mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-4">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                data-testid={`mobile-nav-link-${link.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `text-base font-semibold py-1 ${
                    isActive ? "text-[#d90429]" : "text-gray-900"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <a
              href={CONTACT.phoneHref}
              className="flex items-center gap-2 text-sm font-semibold text-gray-900 py-1"
              data-testid="mobile-phone-link"
            >
              <Phone className="w-4 h-4" />
              {CONTACT.phone}
            </a>
            <Link
              to="/schedule"
              data-testid="mobile-signup-cta"
              className="mt-2 text-center rounded-md bg-[#d90429] px-5 py-3 text-sm font-bold text-white hover:bg-black transition-colors"
            >
              Sign Up for a Class
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="bg-[#0a0a0a] text-white mt-24"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid gap-12 md:grid-cols-4">
        <div className="md:col-span-2">
          <div className="bg-white rounded-md p-3 inline-block">
            <img src={IMAGES.logo} alt="Rhythm Check Training" className="h-10 w-auto" />
          </div>
          <p className="mt-6 text-gray-300 max-w-md leading-relaxed">
            Rhythm Check Training delivers hands-on, AHA-aligned life support and
            workplace safety training across the {CONTACT.area}.
          </p>
        </div>
        <div>
          <div className="font-mono-label text-gray-400 mb-4">Classes</div>
          <ul className="space-y-2 text-sm text-gray-300">
            <li><Link to="/classes/bls" className="hover:text-white">BLS</Link></li>
            <li><Link to="/classes/acls" className="hover:text-white">ACLS</Link></li>
            <li><Link to="/classes/pals" className="hover:text-white">PALS</Link></li>
            <li><Link to="/classes/heartsaver" className="hover:text-white">Heartsaver CPR / First Aid</Link></li>
            <li><Link to="/classes/workplace-safety" className="hover:text-white">Workplace Safety</Link></li>
          </ul>
        </div>
        <div>
          <div className="font-mono-label text-gray-400 mb-4">Contact</div>
          <ul className="space-y-2 text-sm text-gray-300">
            <li>{CONTACT.area}</li>
            <li>
              <a href={CONTACT.phoneHref} className="hover:text-white">
                {CONTACT.phone}
              </a>
            </li>
            <li>{CONTACT.hours}</li>
            <li className="pt-4">
              <Link to="/schedule" className="text-white font-bold hover:text-[#d90429]">
                Sign Up for a Class →
              </Link>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row gap-3 justify-between text-xs text-gray-400">
          <div>© {new Date().getFullYear()} Rhythm Check Training. All rights reserved.</div>
          <div className="flex gap-6">
            <Link to="/policies" className="hover:text-white">Policies</Link>
            <Link to="/admin/login" className="hover:text-white" data-testid="footer-admin-link">
              Admin
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-white text-gray-900">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
